// src/components/EventsPage.tsx

import React, { useState, useEffect } from 'react';
import api from '../services/api'; 
import { toast } from 'sonner'; 
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion'; // Animasyon kütüphanesi eklendi

// UI Bileşenleri
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.tsx';
import { Button } from './ui/button.tsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs.tsx';
import { Input } from './ui/input.tsx'; 
import { Label } from './ui/label.tsx';
import { Calendar, MapPin, Users, Clock, X, Pencil, Trash2, CheckCircle, Plus } from 'lucide-react';
import { ImageWithFallback } from './resim/ImageWithFallback.tsx';

// --- TİP TANIMLAMALARI ---
interface Event {
  id: number;
  Title: string;       
  Description: string; 
  Location: string;    
  StartDate: string;   
  EndDate: string;     
  Image?: string | null; 
  attendees_count?: number;
  is_registered?: boolean; // Backend'den gelen yeni alan
}

// --- YARDIMCI BİLEŞEN: EVENT ACTION BUTTONS ---
// Kayıt olma, düzenleme ve iptal işlemlerini bu bileşen yönetir
const EventActionButtons = ({ event, onRefresh }: { event: Event, onRefresh: () => void }) => {
  const navigate = useNavigate();
  const isLoggedIn = !!localStorage.getItem('access');
  
  // State'ler
  const [isRegistered, setIsRegistered] = useState(event.is_registered || false);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Form verileri
  const [guestCount, setGuestCount] = useState(0);
  const [note, setNote] = useState("");

  // Modal açıldığında mevcut bilgileri çek (Eğer kayıtlıysa)
  const openModal = async () => {
    if (!isLoggedIn) {
        toast.info("Kayıt olmak için lütfen giriş yapınız.");
        navigate('/uyelik?tab=giris');
        return;
    }

    if (isRegistered) {
        // Kayıtlıysa, mevcut bilgileri getir
        try {
            const response = await api.get(`/events/${event.id}/my-registration/`);
            setGuestCount(response.data.GuestCount || 0);
            setNote(response.data.note || ""); // Backend'de note alanı varsa
        } catch (error) {
            console.error("Kayıt detayları alınamadı", error);
        }
    } else {
        // Yeni kayıt ise sıfırla
        setGuestCount(0);
        setNote("");
    }
    setShowModal(true);
  };

  // 1. KAYIT OLMA (POST)
  const handleRegister = async () => {
    setLoading(true);
    try {
        await api.post('/events/register/', {
            event: event.id,
            GuestCount: guestCount,
            note: note 
        });
        toast.success('Kaydınız başarıyla oluşturuldu!');
        setIsRegistered(true);
        setShowModal(false);
        onRefresh(); // Ana listeyi yenile
    } catch (error: any) {
        if (error.response?.status === 400) {
             toast.error('Bu etkinliğe zaten kayıtlısınız.');
             setIsRegistered(true); // Durumu düzelt
        } else {
             toast.error('Kayıt başarısız oldu.');
        }
    } finally {
        setLoading(false);
    }
  };

  // 2. GÜNCELLEME (PATCH/PUT)
  const handleUpdate = async () => {
    setLoading(true);
    try {
        await api.patch(`/events/${event.id}/my-registration/`, {
            GuestCount: guestCount,
            note: note
        });
        toast.success('Kayıt bilgileriniz güncellendi.');
        setShowModal(false);
        onRefresh();
    } catch (error) {
        toast.error('Güncelleme başarısız oldu.');
    } finally {
        setLoading(false);
    }
  };

  // 3. SİLME / İPTAL (DELETE)
  const handleDelete = async () => {
      if (!window.confirm("Kaydınızı iptal etmek istediğinize emin misiniz?")) return;
      
      setLoading(true);
      try {
          await api.delete(`/events/${event.id}/my-registration/`);
          toast.success('Kaydınız iptal edildi.');
          setIsRegistered(false);
          setShowModal(false);
          onRefresh();
      } catch (error) {
          toast.error('İptal işlemi başarısız oldu.');
      } finally {
          setLoading(false);
      }
  };

  return (
    <>
      <div className="mt-auto pt-4">
        {isRegistered ? (
          // DURUM: KAYITLI
          <div className="flex flex-col gap-2">
             <div className="flex items-center justify-center gap-2 text-green-600 bg-green-50 p-2 rounded border border-green-200 text-sm font-medium">
                <CheckCircle className="w-4 h-4" />
                <span>Kayıtlısınız</span>
             </div>
             <Button 
                variant="outline" 
                onClick={openModal}
                className="w-full flex items-center justify-center gap-2"
             >
                <Pencil className="w-3 h-3" /> Düzenle / Kaydı Sil
             </Button>
          </div>
        ) : (
          // DURUM: KAYITSIZ
          <Button 
            className="w-full "
            onClick={openModal}
          >
             <Plus className="w-4 h-4 mr-2" /> Kayıt Ol
          </Button>
        )}
      </div>

      {/* --- MODAL --- */}
      {showModal && (
         <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm animate-in fade-in duration-200">
            <Card className="w-full max-w-sm bg-white shadow-2xl">
                <CardHeader className="flex flex-row justify-between items-start pb-2">
                    <div>
                        <CardTitle className="text-xl">
                            {isRegistered ? 'Kaydı Düzenle' : 'Etkinlik Kaydı'}
                        </CardTitle>
                        <CardDescription>
                            {event.Title}
                        </CardDescription>
                    </div>
                    <Button variant="ghost" size="icon" className="-mt-2 -mr-2" onClick={() => setShowModal(false)}>
                        <X className="h-5 w-5" />
                    </Button>
                </CardHeader>
                <CardContent className="space-y-4 pt-4">
                    {/* Misafir Sayısı Input */}
                    <div className="space-y-2">
                        <Label htmlFor="guestCount">Misafir Sayısı (Siz hariç)</Label>
                        <Input 
                            id="guestCount"
                            type="number" 
                            min="0" 
                            max="10" 
                            value={guestCount}
                            onChange={(e) => setGuestCount(parseInt(e.target.value) || 0)}
                        />
                    </div>

                    {/* Aksiyon Butonları */}
                    <div className="flex gap-2 pt-2">
                        {isRegistered ? (
                            // --- KULLANICI ZATEN KAYITLIYSA ---
                            <>
                                <Button 
                                    onClick={handleUpdate} 
                                    disabled={loading} 
                                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                                >
                                    {loading ? '...' : 'Güncelle'}
                                </Button>

                                {/* İPTAL BUTONU */}
                                <Button 
                                    onClick={handleDelete} 
                                    disabled={loading} 
                                    className="flex items-center gap-2 px-4 bg-red-600 hover:bg-red-700 text-white"
                                >
                                    <Trash2 className="h-4 w-4" />
                                    <span>Kaydı Sil</span>
                                </Button>
                            </>
                        ) : (
                            // --- KULLANICI KAYITLI DEĞİLSE ---
                            <Button 
                                onClick={handleRegister} 
                                disabled={loading} 
                                className="w-full bg-green-600 hover:bg-green-700 text-white"
                            >
                                {loading ? '...' : 'Kaydı Tamamla'}
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>
         </div>
      )}
    </>
  );
};


// --- ANA SAYFA BİLEŞENİ ---
export function EventsPage() {
  const navigate = useNavigate();

  // --- STATE'LER ---
  const [upcomingEvents, setUpcomingEvents] = useState<Event[]>([]);
  const [pastEvents, setPastEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  // --- YARDIMCI FORMAT FONKSİYONLARI ---
  const formatDate = (dateString: string) => {
      if (!dateString) return "";
      const safeDate = dateString.replace(" ", "T");
      const date = new Date(safeDate);
      return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const formatTime = (dateString: string) => {
    if (!dateString) return "";
    const safeDate = dateString.replace(" ", "T");
    const date = new Date(safeDate);
    return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  };

  // --- VERİ ÇEKME ---
  const fetchEvents = async () => {
    try {
      const response = await api.get('/events/');
      const allEvents: Event[] = response.data;
      
      const currentDate = new Date().getTime();

      // 1. Yaklaşan Etkinlikler
      const upcoming = allEvents.filter(event => 
        new Date(event.StartDate).getTime() >= currentDate
      );

      // 2. Geçmiş Etkinlikler
      const past = allEvents.filter(event => 
        new Date(event.StartDate).getTime() < currentDate
      );

      setUpcomingEvents(upcoming);
      setPastEvents(past);

    } catch (error) {
      console.error("Etkinlik verileri çekilemedi:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  if (loading) {
    return <div className="text-center py-10 text-slate-500 animate-pulse">Etkinlikler Yükleniyor...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8 space-y-8 relative">
    
      {/* Sekmeler */}
      <Tabs defaultValue="yaklasan" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="yaklasan">Yaklaşan Etkinlikler</TabsTrigger>
          <TabsTrigger value="gecmis">Geçmiş Etkinlikler</TabsTrigger>
        </TabsList>

        {/* --- YAKLAŞAN ETKİNLİKLER --- */}
        <TabsContent value="yaklasan" className="mt-8">
          {upcomingEvents.length === 0 ? (
             <motion.div 
               initial={{ opacity: 0, y: 20 }} 
               animate={{ opacity: 1, y: 0 }} 
               transition={{ duration: 0.5 }}
               className="text-center py-12 bg-slate-50 rounded-lg border border-dashed border-slate-300"
             >
                <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Şu an planlanan yaklaşan etkinlik bulunmamaktadır.</p>
             </motion.div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcomingEvents.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ duration: 0.4, delay: index * 0.1 }} // Sırayla gelmesi için delay eklendi
                  className="h-full"
                >
                  <Card className="hover:shadow-lg transition-shadow overflow-hidden flex flex-col h-full">
                    <div className="h-48 overflow-hidden relative group">
                      <ImageWithFallback
                        src={event.Image || 'https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=800'}
                        alt={event.Title}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105 duration-500"
                      />
                    </div>
                    
                    <CardHeader>
                      <CardTitle className="line-clamp-1" title={event.Title}>{event.Title}</CardTitle>
                      <CardDescription className="flex items-center gap-1">
                          <MapPin className="h-3 w-3 flex-shrink-0" /> 
                          {event.Location && (event.Location.startsWith('http://') || event.Location.startsWith('https://')) ? (
                              <a 
                                  href={event.Location}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline font-medium line-clamp-1"
                                  title="Haritada Göster"
                              >
                                  Haritada Göster 
                              </a>
                          ) : (
                              <span className="line-clamp-1" title={event.Location}>
                                  {event.Location || 'Konum Belirtilmedi'}
                              </span>
                          )}
                      </CardDescription>
                    </CardHeader>
                    
                    <CardContent className="space-y-4 flex-1 flex flex-col">
                      <div className="space-y-2 text-sm text-slate-600 flex-1">
                          <p className="line-clamp-3">{event.Description}</p>
                          
                          <div className="flex items-center gap-2 pt-2 text-blue-600 font-medium flex-wrap">
                              {/* TARİH */}
                              <div className="flex items-center gap-1">
                                  <Calendar className="h-4 w-4" />
                                  <span>{formatDate(event.StartDate)}</span>
                              </div>
                              <span className="text-slate-300 hidden sm:inline">|</span>
                              {/* YENİ DÜZELTİLMİŞ KOD BÖLÜMÜ */}
                              <div className="flex items-center gap-1">
                                  <Clock className="h-4 w-4 flex-shrink-0" />
                                  <span>
                                    {/* Önce her halükarda Başlangıç Saatini gösteriyoruz */}
                                    {formatTime(event.StartDate)}
                                    
                                    {/* Eğer bitiş saeti varsa ve başlangıç saatinden farklıysa yanına ekliyoruz */}
                                    {event.EndDate && event.EndDate !== event.StartDate && (
                                      <span className="h-4 w-4 flex-shrink-0"> - {formatTime(event.EndDate)}</span>
                                    )}
                                  </span>
                              </div>
                          </div>
                      </div>

                      {/* --- YENİ BİLEŞEN: Action Buttons --- */}
                      <EventActionButtons event={event} onRefresh={fetchEvents} />

                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* --- GEÇMİŞ ETKİNLİKLER --- */}
        <TabsContent value="gecmis" className="mt-8">
          {pastEvents.length === 0 ? (
             <motion.p 
               initial={{ opacity: 0 }} 
               animate={{ opacity: 1 }} 
               className="text-slate-500 mt-4 text-center"
             >
               Geçmiş etkinlik bulunmamaktadır.
             </motion.p>
          ) : (
            <div className="grid md:grid-cols-3 gap-6">
              {pastEvents.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <Card className="hover:shadow transition-shadow opacity-75 hover:opacity-100">
                    <div className="h-40 overflow-hidden grayscale hover:grayscale-0 transition-all">
                      <ImageWithFallback
                          src={event.Image || 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=800'}
                          alt={event.Title}
                          className="w-full h-full object-cover"
                      />
                    </div>
                    <CardHeader>
                      <CardTitle className="text-lg">{event.Title}</CardTitle>
                      <CardDescription className="text-xs flex items-center gap-1">
                          <Calendar className="h-3 w-3"/> {formatDate(event.StartDate)}
                          <Clock className="h-3 w-3 ml-2"/> {formatTime(event.StartDate)}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                          <Users className="h-4 w-4" />
                          <span>{event.attendees_count || 0} kişi katıldı</span>
                      </div>
                      <Button variant="outline" className="w-full" disabled>
                        Etkinlik Sona Erdi
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

    </div>
  );
}